import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, InstagramLogo, ArrowRight } from "@phosphor-icons/react";
import { BrandLink } from "./Brand";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Student", to: "/student" },
  { label: "Parent", to: "/parent" },
  { label: "Property Manager", to: "/property-manager" },
  { label: "FAQ", to: "/faq" },
];

const IG = "https://instagram.com/onelease.app";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-[0_8px_30px_-18px_rgba(37,99,235,0.35)]" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <BrandLink textClass="text-xl sm:text-2xl" markClass="h-8 w-8 sm:h-9 sm:w-9" />

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-[15px] font-medium rounded-full transition-colors duration-200 ${
                    isActive ? "text-ol-blue" : "text-ol-ink/70 hover:text-ol-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href={IG}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              className="ml-2 grid place-items-center h-10 w-10 rounded-full text-ol-ink/70 hover:text-white hover:bg-ol-blue transition-colors duration-200"
              aria-label="OneLease on Instagram"
            >
              <InstagramLogo size={20} weight="bold" />
            </a>
            <Link
              to="/waitlist"
              data-testid="header-waitlist-cta"
              className="ml-2 group inline-flex items-center gap-2 rounded-full bg-ol-blue px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_-8px_rgba(37,99,235,0.6)] hover:bg-ol-deep transition-colors duration-200"
            >
              Join the Waitlist
              <ArrowRight size={16} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </nav>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden grid place-items-center h-11 w-11 rounded-full bg-white/70 backdrop-blur border border-gray-100 text-ol-ink"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden mx-4 mb-4 rounded-3xl bg-white shadow-[0_24px_60px_-20px_rgba(15,23,42,0.3)] border border-gray-100 overflow-hidden"
            data-testid="mobile-nav"
          >
            <div className="p-3">
              {NAV.map((n, i) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-4 py-4 rounded-2xl text-lg font-semibold border-b border-gray-50 last:border-0 ${
                      isActive ? "text-ol-blue bg-ol-surface" : "text-ol-ink"
                    }`
                  }
                >
                  {n.label}
                  <ArrowRight size={18} weight="bold" className="opacity-40" />
                </NavLink>
              ))}
              <div className="flex items-center gap-3 p-3 pt-4">
                <a
                  href={IG}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-instagram"
                  className="grid place-items-center h-12 w-12 rounded-2xl bg-ol-surface text-ol-blue"
                  aria-label="Instagram"
                >
                  <InstagramLogo size={22} weight="bold" />
                </a>
                <Link
                  to="/waitlist"
                  data-testid="mobile-waitlist-cta"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl bg-ol-blue px-5 py-4 text-base font-semibold text-white"
                >
                  Join the Waitlist <ArrowRight size={18} weight="bold" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
